import { config } from '@/config/env'

/**
 * Documentos adjuntos de una herramienta (CR-01).
 *
 * La subida va por XHR y no por el cliente HTTP del sistema porque `fetch` no
 * reporta avance: la barra de progreso de cada tarjeta necesita el evento
 * `upload.onprogress`, que solo existe en XHR.
 */

/** Lo que el formulario manda al guardar la herramienta. */
export interface DocumentoAdjunto {
  fileUrl: string
  fileName: string
  fileSize: number
  mimeType: string
}

/** Un documento ya guardado, como lo devuelve el detalle. */
export interface DocumentoGuardado extends DocumentoAdjunto {
  id: string
  uploadedByUserName: string | null
  createdAt: string
}

export const EXTENSIONES_ACEPTADAS =
  '.pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png'

/** Lo mismo que valida el servidor. Aquí solo evita un viaje de ida y vuelta. */
const EXTENSIONES = [
  'pdf',
  'doc',
  'docx',
  'xls',
  'xlsx',
  'jpg',
  'jpeg',
  'png',
] as const

export const TAMANO_MAXIMO_BYTES = 10 * 1024 * 1024

export const MAXIMO_POR_HERRAMIENTA = 10

export const extensionDe = (nombre: string): string => {
  const partes = nombre.split('.')
  return partes.length > 1 ? (partes.pop() as string).toLowerCase() : ''
}

export const esExtensionValida = (nombre: string): boolean =>
  (EXTENSIONES as readonly string[]).includes(extensionDe(nombre))

export const esImagen = (mimeType: string): boolean =>
  mimeType.startsWith('image/')

/** «PDF · 1.4 MB», que es lo que va debajo del nombre en la tarjeta. */
export const describirArchivo = (nombre: string, bytes: number): string => {
  const ext = extensionDe(nombre).toUpperCase()
  const mb = bytes / (1024 * 1024)
  const peso = mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`
  return `${ext || 'Archivo'} · ${peso}`
}

/**
 * El 413 no lo devuelve la aplicación sino el nginx que está delante, con su
 * `client_max_body_size` en 10 MB desde el 11/09/2026 (antes estaba en 1 MB,
 * su valor por omisión, y cortaba casi todo).
 *
 * Con el tope alineado al del formulario esto ya casi no debería verse: el
 * navegador descarta lo que pasa de 10 MB antes de mandarlo. Si aparece, es que
 * los dos límites se volvieron a desalinear, y el mensaje lo dice sin rodeos.
 */
const MENSAJE_413 =
  'El servidor rechazó el archivo por su tamaño. El límite es de 10 MB.'

export function subirDocumento(
  archivo: File,
  opciones: { alAvanzar?: (porcentaje: number) => void; senal?: AbortSignal } = {},
): Promise<DocumentoAdjunto> {
  const { alAvanzar, senal } = opciones

  return new Promise((resolver, rechazar) => {
    const cuerpo = new FormData()
    cuerpo.append('file', archivo)

    const xhr = new XMLHttpRequest()
    xhr.open('POST', `${config.apiBaseUrl}/almacen/upload/documento`)

    const token = localStorage.getItem('access_token')
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`)

    xhr.upload.onprogress = (evento) => {
      if (!evento.lengthComputable || !alAvanzar) return
      // Se topa en 99: el 100 se pinta cuando el servidor contesta, no cuando
      // se termino de mandar. Entre una cosa y otra pasa el guardado en R2.
      alAvanzar(Math.min(99, Math.round((evento.loaded / evento.total) * 100)))
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const datos = JSON.parse(xhr.responseText)
          alAvanzar?.(100)
          resolver({
            fileUrl: datos.url,
            fileName: datos.fileName ?? archivo.name,
            fileSize: datos.fileSize ?? archivo.size,
            mimeType: datos.mimeType,
          })
        } catch {
          rechazar(new Error('El servidor devolvió una respuesta inesperada.'))
        }
        return
      }

      if (xhr.status === 413) {
        rechazar(new Error(MENSAJE_413))
        return
      }
      if (xhr.status === 401) {
        rechazar(new Error('Tu sesión expiró. Vuelve a entrar e inténtalo otra vez.'))
        return
      }

      let mensaje = 'No se pudo subir el archivo.'
      try {
        const datos = JSON.parse(xhr.responseText)
        const m = datos?.message
        if (m) mensaje = Array.isArray(m) ? m.join(' ') : String(m)
      } catch {
        /* sin cuerpo util: se queda el mensaje generico */
      }
      rechazar(new Error(mensaje))
    }

    // Un corte de red cae aquí sin código de estado. Pasa seguido con archivos
    // grandes y conexiones inestables, así que el mensaje lo dice.
    xhr.onerror = () =>
      rechazar(new Error('Se perdió la conexión durante la subida. Inténtalo de nuevo.'))
    xhr.onabort = () => rechazar(new DOMException('Subida cancelada', 'AbortError'))

    senal?.addEventListener('abort', () => xhr.abort(), { once: true })

    xhr.send(cuerpo)
  })
}
